import { useState, useRef, useEffect } from "react";
import { batchImportListings } from "../../../utils/api";
import { getCategories } from "../../../utils/adminApi";

interface BatchImportModalProps {
  onClose: () => void;
  onSuccess: (count: number) => void;
}

interface ImportRow {
  title: string;
  category_id: string;
  company_name: string;
  contact_name: string;
  contact_phone: string;
  contact_wechat: string;
  content: string;
  images: string;
}

const EMPTY_ROW: ImportRow = {
  title: "",
  category_id: "",
  company_name: "",
  contact_name: "",
  contact_phone: "",
  contact_wechat: "",
  content: "",
  images: "",
};

const EXAMPLE_ROW: ImportRow = {
  title: "专业空调维修服务",
  category_id: "",
  company_name: "XX制冷设备有限公司",
  contact_name: "张三",
  contact_phone: "13800138000",
  contact_wechat: "zhangsan_wx",
  content: "10年专业空调维修经验，覆盖全市各区，免费上门检测，修不好不收费。",
  images: "/uploads/2026/example1.jpg, /uploads/2026/example2.jpg",
};

const FIELD_NAMES: { key: keyof ImportRow; label: string; required: boolean; isCategory?: boolean; aliases?: string[] }[] = [
  { key: "title", label: "标题", required: true, aliases: ["名称", "服务名称", "信息标题", "title"] },
  { key: "category_id", label: "分类", required: false, isCategory: true, aliases: ["所属分类", "类别", "category"] },
  { key: "company_name", label: "公司名称", required: false, aliases: ["公司", "企业名称", "company"] },
  { key: "contact_name", label: "联系人", required: false, aliases: ["姓名", "联系人姓名", "contact"] },
  { key: "contact_phone", label: "电话", required: false, aliases: ["联系电话", "手机号", "手机", "phone", "tel"] },
  { key: "contact_wechat", label: "微信", required: false, aliases: ["微信号", "wechat"] },
  { key: "content", label: "详情", required: false, aliases: ["内容", "服务详情", "描述", "description", "content"] },
  { key: "images", label: "图片", required: false, aliases: ["图片链接", "图片地址", "images"] },
];

function generateCSV(categoryName: string): string {
  const headers = FIELD_NAMES.map((f) => f.label).join(",");
  const exampleValues: Record<string, string> = {
    title: EXAMPLE_ROW.title,
    category_id: categoryName,
    company_name: EXAMPLE_ROW.company_name,
    contact_name: EXAMPLE_ROW.contact_name,
    contact_phone: EXAMPLE_ROW.contact_phone,
    contact_wechat: EXAMPLE_ROW.contact_wechat,
    content: EXAMPLE_ROW.content,
    images: EXAMPLE_ROW.images,
  };
  const example = FIELD_NAMES.map((f) => `"${exampleValues[f.key] || ""}"`).join(",");
  const emptyRow = FIELD_NAMES.map(() => '""').join(",");
  return `${headers}\n${example}\n${emptyRow}`;
}

/**
 * 解析 CSV 文本为二维数组，正确处理引号包裹的多行字段
 * （详情内容中的换行符不会被误当作行分隔符）
 */
function parseCSVRows(text: string): string[][] {
  // 统一换行符格式
  const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const rows: string[][] = [];
  let currentRow: string[] = [];
  let currentField = "";
  let inQuotes = false;

  for (let i = 0; i < normalized.length; i++) {
    const ch = normalized[i];

    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < normalized.length && normalized[i + 1] === '"') {
          // 转义的引号 "" → "
          currentField += '"';
          i++;
        } else {
          // 引号结束
          inQuotes = false;
        }
      } else {
        // 引号内所有字符（包括换行符）都保留
        currentField += ch;
      }
    } else {
      if (ch === '"' && currentField === "") {
        // 字段开始的引号
        inQuotes = true;
      } else if (ch === ',') {
        // 字段分隔符
        currentRow.push(currentField.trim());
        currentField = "";
      } else if (ch === '\n') {
        // 行分隔符（仅在引号外时生效）
        currentRow.push(currentField.trim());
        // 跳过纯空行，但保留表头行
        if (currentRow.some((f) => f !== "")) {
          rows.push(currentRow);
        }
        currentRow = [];
        currentField = "";
      } else {
        currentField += ch;
      }
    }
  }

  // 处理最后一个字段/行
  currentRow.push(currentField.trim());
  if (currentRow.some((f) => f !== "")) {
    rows.push(currentRow);
  }

  return rows;
}

function parseCSV(text: string): { rows: ImportRow[]; error?: string } {
  // 去除 BOM 头
  const cleanText = text.replace(/^\uFEFF/, "");
  if (!cleanText.trim()) return { rows: [], error: "文件内容为空" };

  // 使用支持多行字段的解析器，替换原来的 split(/\r?\n/)
  const allRows = parseCSVRows(cleanText);
  if (allRows.length < 2) return { rows: [], error: "文件至少需要表头行和一行数据" };

  // 表头行：去掉首尾引号
  const rawHeaders = allRows[0].map((h) => h.replace(/^"|"$/g, "").trim());
  const headers = rawHeaders.filter((h) => h.length > 0);

  if (headers.length === 0) return { rows: [], error: "未检测到表头，请确认第一行包含列名（如：标题,分类,联系人...）" };

  // 灵活匹配表头：支持精确匹配 + 别名匹配（不区分大小写）
  const headerMap: Map<string, number> = new Map();
  const matchedFields: string[] = [];
  headers.forEach((h, i) => {
    const hLower = h.toLowerCase();
    FIELD_NAMES.forEach((f) => {
      const candidates = [f.label.toLowerCase(), ...(f.aliases || []).map((a) => a.toLowerCase())];
      if (candidates.includes(hLower)) {
        headerMap.set(f.key, i);
        matchedFields.push(f.label);
      }
    });
  });

  // 检查是否匹配到标题列
  if (!headerMap.has("title")) {
    return {
      rows: [],
      error: `未找到"标题"列。当前表头: ${headers.join(", ")}。请确保包含"标题"列，可下载模板查看正确格式。`,
    };
  }

  const rows: ImportRow[] = [];
  for (let i = 1; i < allRows.length; i++) {
    const cols = allRows[i];
    // 跳过空行（所有字段都为空）
    if (cols.every((c) => !c)) continue;
    const row: ImportRow = { ...EMPTY_ROW };
    headerMap.forEach((colIdx, key) => {
      if (colIdx < cols.length) {
        (row as any)[key] = cols[colIdx].replace(/^"|"$/g, "").trim();
      }
    });
    if (row.title) rows.push(row);
  }

  if (rows.length === 0) {
    return {
      rows: [],
      error: `已识别表头: ${matchedFields.join(", ")}，但未解析到有效数据行。请确保数据行中"标题"不为空。`,
    };
  }

  return { rows };
}

export default function BatchImportModal({ onClose, onSuccess }: BatchImportModalProps) {
  const [rows, setRows] = useState<ImportRow[]>([{ ...EMPTY_ROW }]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [categories, setCategories] = useState<Array<{ id: string; name: string }>>([]);
  // 使用 ref 保持 resolveCategory 始终读取最新分类数据（避免闭包过期问题）
  const categoriesRef = useRef<Array<{ id: string; name: string }>>([]);

  useEffect(() => {
    (async () => {
      try {
        const cats = await getCategories();
        const flatList: Array<{ id: string; name: string }> = [];
        const flatten = (items: any[]) => {
          for (const item of items) {
            flatList.push({ id: item.id, name: item.name });
            if (item.children && item.children.length > 0) {
              flatten(item.children);
            }
          }
        };
        flatten(cats);
        setCategories(flatList);
        categoriesRef.current = flatList;
      } catch {
        // ignore
      }
    })();
  }, []);

  // 分类数据加载完成后，自动将已导入表格中的分类名称转为 ID
  useEffect(() => {
    if (categories.length > 0 && rows.length > 0) {
      setRows((prev) =>
        prev.map((row) => {
          if (!row.category_id) return row;
          // 已是 UUID 则跳过
          if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(row.category_id)) return row;
          const resolved = resolveCategory(row.category_id);
          return resolved ? { ...row, category_id: resolved } : row;
        })
      );
    }
    // 仅在 categories 首次加载后执行一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  function resolveCategory(raw: string): string {
    if (!raw) return "";
    // 始终使用最新的分类数据
    const cats = categoriesRef.current;
    // If it looks like a UUID, assume it's already an ID (from select)
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)) {
      return raw;
    }
    // Otherwise, try to match by name
    const trimmed = raw.trim();
    const match = cats.find((c) => c.name === trimmed);
    if (match) return match.id;
    // Try partial match
    const partial = cats.find((c) => c.name.includes(trimmed) || trimmed.includes(c.name));
    if (partial) return partial.id;
    return "";
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  function updateRow(index: number, field: keyof ImportRow, value: string) {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  }

  function addRow() {
    setRows((prev) => [...prev, { ...EMPTY_ROW }]);
  }

  function removeRow(index: number) {
    if (rows.length <= 1) return;
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  function handleDownloadTemplate() {
    const categoryName = categories.length > 0 ? categories[0].name : "未分类";
    const csv = generateCSV(categoryName);
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "批量导入模板.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async () => {
      const buffer = reader.result as ArrayBuffer;
      // 先尝试 UTF-8 解码，再尝试 GBK（Excel 导出 CSV 常用编码）
      const decoders: Array<{ name: string; decode: (buf: ArrayBuffer) => string }> = [
        { name: "utf-8", decode: (buf) => new TextDecoder("utf-8").decode(buf) },
        { name: "gbk", decode: (buf) => new TextDecoder("gbk").decode(buf) },
      ];

      let lastError = "";
      for (const dec of decoders) {
        try {
          const text = dec.decode(buffer);
          const { rows: parsed, error } = parseCSV(text);
          if (!error && parsed.length > 0) {
            // 自动将 CSV 中的分类名称转换为分类 ID
            const resolvedRows = parsed.map((row) => ({
              ...row,
              category_id: row.category_id ? resolveCategory(row.category_id) : "",
            }));
            setRows(resolvedRows);
            showToast(`已解析 ${parsed.length} 条数据（${dec.name.toUpperCase()} 编码）`);
            return;
          }
          lastError = error || lastError;
        } catch {
          continue;
        }
      }
      showToast(lastError || "未解析到有效数据，请检查文件格式");
    };
    reader.readAsArrayBuffer(file);
    // Reset input
    e.target.value = "";
  }

  async function handleSubmit() {
    const validRows = rows.filter((r) => r.title.trim());
    if (validRows.length === 0) {
      showToast("请至少填写一条标题");
      return;
    }

    setLoading(true);
    try {
      const data = validRows.map((r) => ({
        title: r.title.trim(),
        category_id: resolveCategory(r.category_id),
        company_name: r.company_name.trim(),
        contact_name: r.contact_name.trim(),
        contact_phone: r.contact_phone.trim(),
        contact_wechat: r.contact_wechat.trim(),
        content: r.content.trim(),
        images: r.images
          .split(/[,，]/)
          .map((s) => s.trim())
          .filter(Boolean),
      }));

      const result = await batchImportListings(data);
      onSuccess(result.count);
    } catch (e: any) {
      showToast(e?.message || "批量导入失败");
    } finally {
      setLoading(false);
    }
  }

  const totalRows = rows.length;
  const validRows = rows.filter((r) => r.title.trim()).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-5xl mx-4 max-h-[90vh] flex flex-col overflow-hidden shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Toast */}
        {toast && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 bg-gray-800 text-white text-sm px-4 py-2 rounded-xl whitespace-nowrap">
            <i className="ri-information-line mr-1.5" />
            {toast}
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-800">批量导入信息</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              支持手动填写或上传 CSV 文件，图片字段支持多个URL用逗号分隔
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
          >
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-gray-50 bg-gray-50/50 shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={addRow}
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-gray-200 text-gray-600 hover:border-orange-400 hover:text-orange-500 rounded-lg cursor-pointer transition-colors"
            >
              <i className="ri-add-line" />
              添加行
            </button>
            <button
              onClick={handleDownloadTemplate}
              className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-gray-200 text-gray-600 hover:border-blue-400 hover:text-blue-500 rounded-lg cursor-pointer transition-colors"
            >
              <i className="ri-download-line" />
              下载模板
            </button>
            <label className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white border border-gray-200 text-gray-600 hover:border-green-400 hover:text-green-500 rounded-lg cursor-pointer transition-colors">
              <i className="ri-upload-line" />
              上传CSV
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <span>
              共 <strong className="text-gray-600">{totalRows}</strong> 行
            </span>
            <span>
              有效 <strong className="text-orange-500">{validRows}</strong> 条
            </span>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-50 z-10">
              <tr className="border-b border-gray-100">
                <th className="px-3 py-2.5 text-left font-medium text-gray-500 w-10">#</th>
                {FIELD_NAMES.map((f) => (
                  <th
                    key={f.key}
                    className={`px-3 py-2.5 text-left font-medium text-gray-500 ${
                      f.key === "title" || f.key === "content" ? "min-w-[140px]" : "min-w-[90px]"
                    } ${f.key === "images" ? "min-w-[160px]" : ""}`}
                  >
                    {f.label}
                    {f.required && <span className="text-red-400 ml-0.5">*</span>}
                  </th>
                ))}
                <th className="px-3 py-2.5 text-left font-medium text-gray-500 w-12">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((row, index) => (
                <tr
                  key={index}
                  className={`hover:bg-gray-50/50 transition-colors ${!row.title.trim() ? "opacity-60" : ""}`}
                >
                  <td className="px-3 py-2 text-gray-400">{index + 1}</td>
                  {FIELD_NAMES.map((f) => (
                    <td key={f.key} className="px-3 py-1.5">
                      {f.key === "category_id" ? (
                        <select
                          value={row.category_id}
                          onChange={(e) => updateRow(index, "category_id", e.target.value)}
                          className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:border-orange-400 bg-white"
                        >
                          <option value="">不选择</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                      ) : f.key === "content" ? (
                        <textarea
                          value={row.content}
                          onChange={(e) => updateRow(index, "content", e.target.value)}
                          placeholder="详情内容..."
                          rows={2}
                          className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:border-orange-400 resize-none"
                        />
                      ) : f.key === "images" ? (
                        <input
                          type="text"
                          value={row.images}
                          onChange={(e) => updateRow(index, "images", e.target.value)}
                          placeholder="图片URL, 逗号分隔"
                          className="w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:border-orange-400"
                        />
                      ) : (
                        <input
                          type="text"
                          value={row[f.key]}
                          onChange={(e) => updateRow(index, f.key, e.target.value)}
                          placeholder={f.required ? `请输入${f.label}` : `${f.label}(选填)`}
                          className={`w-full px-2 py-1.5 text-xs border rounded-md focus:outline-none focus:border-orange-400 ${
                            f.required && !row[f.key].trim() ? "border-red-200 bg-red-50/30" : "border-gray-200"
                          }`}
                        />
                      )}
                    </td>
                  ))}
                  <td className="px-3 py-1.5">
                    <button
                      onClick={() => removeRow(index)}
                      disabled={rows.length <= 1}
                      className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
                    >
                      <i className="ri-delete-bin-line" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Example rows */}
        <div className="border-t border-gray-100 shrink-0 bg-blue-50/30">
          <details className="group">
            <summary className="flex items-center gap-1.5 px-6 py-2.5 text-xs text-blue-600 cursor-pointer hover:text-blue-700 select-none">
              <i className="ri-lightbulb-line" />
              查看示例数据
              <i className="ri-arrow-down-s-line transition-transform group-open:rotate-180" />
            </summary>
            <div className="px-6 pb-3 overflow-x-auto">
              <table className="w-full text-xs border border-blue-100 rounded-lg overflow-hidden">
                <thead>
                  <tr className="bg-blue-100/50">
                    {FIELD_NAMES.map((f) => (
                      <th key={f.key} className="px-3 py-1.5 text-left font-medium text-blue-700 whitespace-nowrap">
                        {f.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-white">
                    {FIELD_NAMES.map((f) => (
                      <td key={f.key} className="px-3 py-1.5 text-gray-600 whitespace-nowrap">
                        {f.key === "category_id"
                          ? categories[0]?.name || "-"
                          : EXAMPLE_ROW[f.key] || "-"}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </details>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
          <span className="text-xs text-gray-400">
            将创建 <strong className="text-orange-500">{validRows}</strong> 条信息（需填写标题）
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2 text-sm text-gray-600 hover:text-gray-800 cursor-pointer"
            >
              取消
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || validRows === 0}
              className="px-5 py-2 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors flex items-center gap-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  导入中...
                </>
              ) : (
                <>
                  <i className="ri-upload-cloud-line" />
                  确认导入 ({validRows}条)
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
